import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type {
  DateRangeValue,
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { PortCode } from "@services/vessel-call/types";
import type {
  VesselNominationCompany,
  VesselNominationSearchRequest,
  VesselNominationsResponse,
} from "@services/vessel-nomination/types";
import { getCompanies, QUERY_KEY, searchVesselNominations } from "@services/vesselNominationApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { convertUaeToUtc, convertUtcToUae, formatDateToDDMMYYYY, toDate } from "@utils/date-time";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { VESSEL_NOMINATION_COLUMNS } from "../utils";
import {
  canRequestVesselNomination,
  getReadableNominationTypes,
} from "../vessel-scan/nominationTypeOptions";
import { getNominationRowPath } from "./getNominationRowPath";

export const LANG_BASE = "vessel-nomination";
export const LANG_BASE_HEADER = `${LANG_BASE}.tableHeaders`;
const TEST_ID_BASE = "vessel-nomination";

const LOCAL_STORAGE_PAGE_KEY = "vessel-nomination-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "vessel-nomination-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "vessel-nomination-filters";
const DEFAULT_PAGE_SIZE = 10;
const EMPTY_USER_ACTION_PERMISSIONS: string[] = [];
const EMPTY_USER_ACCESS_PERMISSIONS: string[] = [];
const VESSEL_NOMINATION_ID_MIN_LENGTH = 1;
const VESSEL_NOMINATION_TEXT_FILTER_MIN_LENGTH = 3;
const VESSEL_SERVICE = "bff-vessel";
const GRID_SEARCH_INQUIRY_ID = "vessel-nomination.grid.search";
const GRID_SEARCH_INQUIRY_CONTEXT = {
  module: "vessel_nomination",
  screen: "Vessel Nomination",
  action: "search_vessel_nominations",
  entityType: "vessel_nomination",
};

const NOMINATION_TYPE_OPTIONS = [
  { label: "Liquid Bulk", value: "LIQUID_BULK" },
  { label: "Dry Bulk", value: "DRY_BULK" },
];

const NOMINATION_STATUS_OPTIONS = [
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Pending Approval", value: "PENDING_APPROVAL" },
];

const formatText = (value?: string | number | null) => {
  if (value == null) {
    return "-";
  }

  const normalizedValue = String(value).trim();

  return normalizedValue ? normalizedValue : "-";
};

const formatEnumLabel = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  return value
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
};

const ISO_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T/;

const formatDateTime = (value?: string | number | null) => {
  if (!value) {
    return "-";
  }

  if (typeof value !== "string" || !ISO_DATE_TIME_PATTERN.test(value)) {
    return String(value);
  }

  try {
    return formatDateToDDMMYYYY(convertUtcToUae(value), true);
  } catch {
    return value;
  }
};

const readStoredFilters = () => {
  const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);

  if (!saved) {
    return [];
  }

  try {
    return JSON.parse(saved) as FilterItem[];
  } catch {
    return [];
  }
};

const buildVesselNominationFilterFields = (
  t: (key: string, options?: { defaultValue?: string }) => string,
  companies: VesselNominationCompany[] = [],
  ports: PortCode[] = [],
  readableNominationTypes: string[] = [],
): Record<string, FilterFieldDefinition> => ({
  refOrderNumber: {
    label: t(`${LANG_BASE_HEADER}.nominationId`, { defaultValue: "Nomination ID" }),
    type: "text",
    minLength: VESSEL_NOMINATION_ID_MIN_LENGTH,
  },
  nominationType: {
    label: t(`${LANG_BASE_HEADER}.nominationType`, { defaultValue: "Nomination Type" }),
    type: "select",
    options: NOMINATION_TYPE_OPTIONS.filter((option) =>
      readableNominationTypes.includes(option.value),
    ),
  },
  imoNumber: {
    label: t(`${LANG_BASE_HEADER}.imoNo.`, { defaultValue: "IMO No." }),
    type: "text",
    minLength: VESSEL_NOMINATION_TEXT_FILTER_MIN_LENGTH,
  },
  vesselName: {
    label: t(`${LANG_BASE_HEADER}.vesselName`, { defaultValue: "Vessel Name" }),
    type: "text",
    minLength: VESSEL_NOMINATION_TEXT_FILTER_MIN_LENGTH,
  },
  port: {
    label: t(`${LANG_BASE_HEADER}.port`, { defaultValue: "Port" }),
    type: "select",
    options: ports
      .filter(({ portCode, portName }) => portCode?.trim() && portName?.trim())
      .sort((a, b) => {
        const nameCompare = a.portName.trim().localeCompare(b.portName.trim());
        if (nameCompare) return nameCompare;
        return a.portCode.trim().localeCompare(b.portCode.trim());
      })
      .map(({ portCode, portName }) => {
        const trimmedPortCode = portCode.trim();

        return {
          label: portName.trim(),
          value: trimmedPortCode,
        };
      }),
  },
  shippingAgency: {
    label: t(`${LANG_BASE_HEADER}.agencyName`, { defaultValue: "Agency Name" }),
    type: "autocomplete",
    options: companies
      .filter(
        ({ companyCode, companyNameEnglish }) => companyCode?.trim() && companyNameEnglish?.trim(),
      )
      .sort((a, b) => a.companyNameEnglish.trim().localeCompare(b.companyNameEnglish.trim()))
      .map(({ companyCode, companyNameEnglish }) => {
        const trimmedCompanyCode = companyCode.trim();

        return {
          label: companyNameEnglish.trim(),
          value: trimmedCompanyCode,
          key: trimmedCompanyCode,
        };
      }),
    inputLengthForDropdown: 1,
    matchFrom: "start",
  },
  eta: {
    label: t(`${LANG_BASE_HEADER}.eta`, { defaultValue: "ETA" }),
    type: "dateFromTo",
  },
  createdAt: {
    label: t(`${LANG_BASE_HEADER}.nominationDateTime`, {
      defaultValue: "Nomination Date/Time",
    }),
    type: "dateFromTo",
  },
  status: {
    label: t(`${LANG_BASE_HEADER}.status`, { defaultValue: "Status" }),
    type: "select",
    options: NOMINATION_STATUS_OPTIONS,
  },
});

const appendTextFilter = (body: Record<string, unknown>, field: string, value: string | number) => {
  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return;
  }

  const currentValues = (body[field] as string[] | undefined) ?? [];
  body[field] = [...currentValues, normalizedValue];
};

const appendDateRangeFilter = (
  body: Record<string, unknown>,
  field: string,
  value: DateRangeValue,
) => {
  const fromDate = toDate(value.from);
  const toDateValue = toDate(value.to);

  if (!fromDate && !toDateValue) {
    return;
  }

  const currentValues =
    (body[field] as
      | Array<{
          from?: string;
          to?: string;
        }>
      | undefined) ?? [];

  body[field] = [
    ...currentValues,
    {
      ...(fromDate ? { from: convertUaeToUtc(fromDate) } : {}),
      ...(toDateValue ? { to: convertUaeToUtc(toDateValue) } : {}),
    },
  ];
};

const toBackendEnumValue = (value: string) =>
  value
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();

const buildVesselNominationFilterBody = (filters: FilterItem[]): VesselNominationSearchRequest => {
  const body: Record<string, unknown> = {};

  for (const filter of filters) {
    if (!filter.field || filter.value == null || filter.value === "") {
      continue;
    }

    switch (filter.field) {
      case "refOrderNumber":
      case "vesselName":
        appendTextFilter(body, filter.field, String(filter.value));
        break;
      case "port":
        appendTextFilter(body, "portCode", String(filter.value));
        break;
      case "nominationType":
        appendTextFilter(body, filter.field, toBackendEnumValue(String(filter.value)));
        break;
      case "shippingAgency":
        appendTextFilter(body, "companyCode", String(filter.value));
        break;
      case "imoNumber":
        appendTextFilter(body, "vesselImo", String(filter.value));
        break;
      case "status":
        appendTextFilter(body, filter.field, toBackendEnumValue(String(filter.value)));
        break;
      case "eta":
        appendDateRangeFilter(body, "rta", filter.value as DateRangeValue);
        break;
      case "createdAt":
        appendDateRangeFilter(body, filter.field, filter.value as DateRangeValue);
        break;
      default:
        break;
    }
  }

  return body as VesselNominationSearchRequest;
};

export const VesselNominationGrid = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userActionPermissions = useAuthStore(
    (state) => state.userActionPermissions ?? EMPTY_USER_ACTION_PERMISSIONS,
  );
  const userAccessPermissions = useAuthStore(
    (state) => state.userAccessPermissions ?? EMPTY_USER_ACCESS_PERMISSIONS,
  );
  const canAddVesselNomination = canRequestVesselNomination(userActionPermissions);
  const readableNominationTypes = useMemo(
    () => getReadableNominationTypes(userAccessPermissions),
    [userAccessPermissions],
  );
  const [filters, setFilters] = useState<FilterItem[]>(() => readStoredFilters());

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_PAGE_SIZE;
  });

  const {
    data,
    isLoading: isVesselNominationsLoading,
    isFetching,
    isError,
    isSuccess,
    dataUpdatedAt,
    error,
  } = useQuery<VesselNominationsResponse>({
    queryKey: [QUERY_KEY, page, pageSize, filters],
    queryFn: () =>
      searchVesselNominations({
        page,
        pageSize,
        body: buildVesselNominationFilterBody(filters),
      }),
    staleTime: 0,
    refetchOnMount: true,
  });

  useTrackPendingInquiry(GRID_SEARCH_INQUIRY_ID, {
    isFetching,
    isLoading: isVesselNominationsLoading,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);
  useEffect(() => {
    if (isVesselNominationsLoading || error || hasTrackedInitialLoad.current) {
      return;
    }
    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Vessel Nomination");
    trackScreenLoad("Vessel Call Management", "Vessel Nomination", startedAt.current);
  }, [isVesselNominationsLoading, error]);

  const { data: companies = [] } = useQuery<VesselNominationCompany[]>({
    queryKey: ["vessel-nomination-companies"],
    queryFn: getCompanies,
  });

  const { options: ports = [] } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORT_CODES,
    VESSEL_SERVICE,
  );

  const headers: Header[] = useMemo(
    () => [
      {
        field: VESSEL_NOMINATION_COLUMNS.NOMINATION_ID,
        columnName: t(`${LANG_BASE_HEADER}.nominationId`, { defaultValue: "Nomination ID" }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.NOMINATION_TYPE,
        columnName: t(`${LANG_BASE_HEADER}.nominationType`, {
          defaultValue: "Nomination Type",
        }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.IMO_NUMBER,
        columnName: t(`${LANG_BASE_HEADER}.imoNo.`, { defaultValue: "IMO No." }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.VESSEL_NAME,
        columnName: t(`${LANG_BASE_HEADER}.vesselName`, { defaultValue: "Vessel Name" }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.PORT,
        columnName: t(`${LANG_BASE_HEADER}.port`, { defaultValue: "Port" }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.AGENCY_NAME,
        columnName: t(`${LANG_BASE_HEADER}.agencyName`, { defaultValue: "Agency Name" }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.ETA,
        columnName: t(`${LANG_BASE_HEADER}.eta`, { defaultValue: "ETA" }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.NOMINATION_DATE_TIME,
        columnName: t(`${LANG_BASE_HEADER}.nominationDateTime`, {
          defaultValue: "Nomination Date/Time",
        }),
      },
      {
        field: VESSEL_NOMINATION_COLUMNS.STATUS,
        columnName: t(`${LANG_BASE_HEADER}.status`, { defaultValue: "Status" }),
      },
    ],
    [t],
  );

  const filterFields = useMemo<Record<string, FilterFieldDefinition>>(
    () => buildVesselNominationFilterFields(t, companies, ports, readableNominationTypes),
    [companies, ports, readableNominationTypes, t],
  );

  const formattedFormData = useMemo(
    () =>
      (data?.items ?? []).map((item) => {
        const nominationType = item.nominationType;
        const status = item.status ?? "";

        return {
          id: item.id,
          rawNominationType: nominationType,
          rawStatus: status,
          [VESSEL_NOMINATION_COLUMNS.NOMINATION_ID]: formatText(item.nominationId),
          [VESSEL_NOMINATION_COLUMNS.NOMINATION_TYPE]: t(
            `${LANG_BASE}.nomination-type-labels.${nominationType ?? ""}`,
            {
              defaultValue: formatEnumLabel(nominationType),
            },
          ),
          [VESSEL_NOMINATION_COLUMNS.IMO_NUMBER]: formatText(item.imoNumber),
          [VESSEL_NOMINATION_COLUMNS.VESSEL_NAME]: formatText(item.vesselName),
          [VESSEL_NOMINATION_COLUMNS.PORT]: formatText(item.port),
          [VESSEL_NOMINATION_COLUMNS.AGENCY_NAME]: formatText(item.agencyName),
          [VESSEL_NOMINATION_COLUMNS.ETA]: formatDateTime(item.eta),
          [VESSEL_NOMINATION_COLUMNS.NOMINATION_DATE_TIME]: formatDateTime(item.createdAt),
          [VESSEL_NOMINATION_COLUMNS.STATUS]: t(`${LANG_BASE}.status-labels.${status}`, {
            defaultValue: formatEnumLabel(status),
          }),
        };
      }),
    [data?.items, t],
  );

  const onApplyFilters = (newFilters: FilterItem[]) => {
    if (page !== 1 || JSON.stringify(newFilters) !== JSON.stringify(filters)) {
      startInquiryTiming(GRID_SEARCH_INQUIRY_ID, GRID_SEARCH_INQUIRY_CONTEXT);
    }
    setFilters(newFilters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(newFilters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (page !== 1 || filters.length > 0) {
      startInquiryTiming(GRID_SEARCH_INQUIRY_ID, GRID_SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={(error as Error).message} />;
  }

  if (isVesselNominationsLoading) {
    return <LoadingOverlay loading={isVesselNominationsLoading} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      {!isVesselNominationsLoading && (
        <CustomDataTable
          testId={TEST_ID_BASE}
          title={t(`${LANG_BASE}.title`)}
          actionName={canAddVesselNomination ? t(`${LANG_BASE}.addVesselNomination`) : ""}
          headers={headers}
          rows={formattedFormData}
          page={page}
          pageSize={pageSize}
          totalCount={data?.totalElements ?? 0}
          canPreview={true}
          onPageChange={(newPage, newSize) => {
            if (newPage !== page || newSize !== pageSize) {
              startInquiryTiming(GRID_SEARCH_INQUIRY_ID, GRID_SEARCH_INQUIRY_CONTEXT);
            }
            setPage(newPage);
            setPageSize(newSize);
            localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
            localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
          }}
          navigationLinkBase="/vessel-nomination/nomination-details"
          onRowClick={(row: {
            id?: string | number;
            rawNominationType?: string | null;
            rawStatus?: string;
          }) => navigate(getNominationRowPath(row))}
          pageSizeOptions={[5, 10, 15, 20, 25]}
          filters={filters}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
          availableFields={filterFields}
        />
      )}
    </div>
  );
};

export default VesselNominationGrid;
